import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { type LucideIcon } from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import { ListItem } from '@components/common';

interface SettingsGroupItem {
  icon: LucideIcon;
  label: string;
  value?: string;
  badge?: string;
  onPress: () => void;
}

interface SettingsGroupProps {
  title: string;
  items: SettingsGroupItem[];
}

export function SettingsGroup({ title, items }: SettingsGroupProps) {
  const { colors } = useTheme();

  return (
    <View style={styles.group}>
      <Text style={[styles.groupTitle, { color: colors.textSecondary }]}>{title}</Text>
      <View style={[styles.groupContent, { borderTopColor: colors.border }]}>
        {items.map((item) => (
          <ListItem
            key={item.label}
            icon={item.icon}
            label={item.label}
            value={item.value}
            badge={item.badge}
            onPress={item.onPress}
            showDivider
            style={styles.row}
          />
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  group: {
    marginBottom: SPACING.md,
  },
  groupTitle: {
    ...TYPOGRAPHY.overline,
    marginBottom: SPACING.sm,
  },
  // Hairline-divided rows, not a bordered card: the group is bounded by a
  // rule above the first row and each row's own rule beneath it (mockup
  // `.rowlink` / `.rowlink:first-of-type`).
  groupContent: {
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  // Rows sit flush in the screen's content column so the hairlines run the
  // full measure instead of being inset inside a box that no longer exists.
  row: {
    paddingHorizontal: 0,
  },
});
